import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import {
  X,
  Download,
  FolderOpen,
  ArrowRight,
  SlidersHorizontal,
  ChevronDown,
  CheckSquare,
  Square,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { downloadQueueManager } from '../../download/DownloadQueueManager.ts';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import { mediaTitle, type HomeMedia } from '../../metadata/HomeRecommendations.ts';
import type { MangaChapterVariant } from './UnifiedMangaChapters.tsx';
import './download-dialog.css';

export function MangaDownloadDialog({
  media,
  rows,
  open,
  onClose,
}: {
  media: HomeMedia;
  rows: MangaChapterVariant[];
  open: boolean;
  onClose: () => void;
}) {
  const [source, setSource] = useState('auto');
  const [from, setFrom] = useState<number | ''>('');
  const [to, setTo] = useState<number | ''>('');
  const [queued, setQueued] = useState<string[]>([]);
  const [queue, setQueue] = useState(downloadQueueManager.getQueue());
  const [error, setError] = useState('');
  const [showAdvancedList, setShowAdvancedList] = useState(false);
  const [manualSelection, setManualSelection] = useState<number[]>([]);

  useEffect(() => {
    const changed = () => setQueue(downloadQueueManager.getQueue());
    window.addEventListener('yomiri-download-queue-changed', changed);
    return () => window.removeEventListener('yomiri-download-queue-changed', changed);
  }, []);

  const numbers = useMemo(() => {
    return [...new Set(rows.map((r) => r.chapter.chapterNumber))].sort((a, b) => a - b);
  }, [rows]);

  const sources = useMemo(() => {
    return [...new Set(rows.map((r) => r.manga.sourceId))];
  }, [rows]);

  useEffect(() => {
    if (open && numbers.length > 0) {
      setFrom(numbers[0]);
      setTo(numbers[numbers.length - 1]);
      setManualSelection(numbers);
      setError('');
      setShowAdvancedList(false);
    }
  }, [open, numbers]);

  const selectedNumbers = useMemo(() => {
    if (showAdvancedList) return manualSelection;
    return numbers.filter((n) => (from === '' || n >= from) && (to === '' || n <= to));
  }, [numbers, from, to, showAdvancedList, manualSelection]);

  const selectQuick = (count: number) => {
    if (!numbers.length) return;
    if (count === -1) {
      setFrom(numbers[0]);
      setTo(numbers[numbers.length - 1]);
      setManualSelection(numbers);
    } else {
      const slice = numbers.slice(0, count);
      setFrom(slice[0]);
      setTo(slice[slice.length - 1]);
      setManualSelection(slice);
    }
  };

  const toggleNumber = (num: number) => {
    setManualSelection((prev) =>
      prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num].sort((a, b) => a - b)
    );
  };

  const enqueue = async () => {
    setError('');
    try {
      const favorite = await yomiriDatabase.getSetting<string>(
        `manga-favorite-source:${media.id || rows[0]?.manga.sourceId + ':' + rows[0]?.manga.id}`
      );
      const ids: string[] = [];

      for (const n of selectedNumbers) {
        const candidates = rows.filter(
          (r) => r.chapter.chapterNumber === n && (source === 'auto' || r.manga.sourceId === source)
        );
        const row = candidates.find((r) => r.manga.sourceId === favorite) || candidates[0];
        if (!row) continue;
        downloadQueueManager.enqueue(row.manga, row.chapter, row.manga.sourceId);
        ids.push(`${row.chapter.sourceId}_${row.chapter.id}`);
      }

      setQueued(ids);
      if (!ids.length) {
        setError('No se encontraron capítulos disponibles en la fuente elegida.');
      }
    } catch {
      setError('No se pudo iniciar la descarga. Reintentá.');
    }
  };

  const tasks = queue.filter((row) => queued.includes(row.id));
  const isBusy = tasks.some((t) => t.status === 'QUEUED' || t.status === 'DOWNLOADING');

  return (
    <Dialog
      className="y-download-dialog yomiri-panel"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle className="y-download-heading">
        <div className="y-dl-title-group">
          <Download size={18} className="y-dl-title-icon" />
          <span>Descargar capítulos</span>
        </div>
        <button
          aria-label="Cerrar ventana de descarga"
          className="y-dl-close-btn"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </DialogTitle>

      <DialogContent className="y-dl-content">
        {/* Work Brief */}
        <div className="y-dl-work-card">
          <img src={media.coverImage.large} alt="" className="y-dl-work-cover" />
          <div className="y-dl-work-meta">
            <h3 className="y-dl-work-title">{mediaTitle(media)}</h3>
            <span className="y-dl-work-subtitle">
              {numbers.length} {numbers.length === 1 ? 'capítulo disponible' : 'capítulos disponibles'}
            </span>
          </div>
        </div>

        {/* Source Selector */}
        <div className="y-dl-control-group">
          <label className="y-dl-field-label" htmlFor="manga-dl-source-select">
            Proveedor / Fuente
          </label>
          <div className="y-dl-select-wrapper">
            <select
              id="manga-dl-source-select"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="y-dl-input"
            >
              <option value="auto">Automática (usa tu favorita o la primera activa)</option>
              {sources.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="y-dl-select-arrow" />
          </div>
        </div>

        {/* Chapters Range Selection */}
        <div className="y-dl-control-group">
          <div className="y-dl-section-bar">
            <span className="y-dl-field-label">Capítulos a descargar</span>
            <span className="y-dl-count-pill">{selectedNumbers.length} seleccionados</span>
          </div>

          <div className="y-dl-presets-bar">
            <button
              type="button"
              className={`y-dl-preset-chip ${selectedNumbers.length === numbers.length ? 'is-active' : ''}`}
              onClick={() => selectQuick(-1)}
            >
              Todos ({numbers.length})
            </button>
            {numbers.length > 5 && (
              <button
                type="button"
                className="y-dl-preset-chip"
                onClick={() => selectQuick(5)}
              >
                Primeros 5
              </button>
            )}
            {numbers.length > 10 && (
              <button
                type="button"
                className="y-dl-preset-chip"
                onClick={() => selectQuick(10)}
              >
                Primeros 10
              </button>
            )}
          </div>

          <div className="y-dl-range-card">
            <div className="y-dl-range-field">
              <label htmlFor="manga-dl-from">Desde capítulo</label>
              <input
                id="manga-dl-from"
                type="number"
                value={from}
                placeholder={String(numbers[0] || 1)}
                onChange={(e) => setFrom(e.target.value === '' ? '' : Number(e.target.value))}
                className="y-dl-input y-dl-range-num"
              />
            </div>
            <ArrowRight size={14} className="y-dl-range-sep" />
            <div className="y-dl-range-field">
              <label htmlFor="manga-dl-to">Hasta capítulo</label>
              <input
                id="manga-dl-to"
                type="number"
                value={to}
                placeholder={String(numbers[numbers.length - 1] || 1)}
                onChange={(e) => setTo(e.target.value === '' ? '' : Number(e.target.value))}
                className="y-dl-input y-dl-range-num"
              />
            </div>
          </div>

          <div className="y-dl-accordion">
            <button
              type="button"
              className="y-dl-accordion-trigger"
              onClick={() => setShowAdvancedList((v) => !v)}
            >
              <SlidersHorizontal size={13} />
              <span>Elegir capítulos manualmente uno por uno</span>
              <ChevronDown
                size={14}
                style={{ transform: showAdvancedList ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              />
            </button>

            {showAdvancedList && (
              <div className="y-dl-grid-selector">
                {numbers.map((num) => {
                  const isChecked = manualSelection.includes(num);
                  return (
                    <button
                      type="button"
                      key={num}
                      className={`y-dl-ep-chip ${isChecked ? 'is-selected' : ''}`}
                      onClick={() => toggleNumber(num)}
                    >
                      {isChecked ? <CheckSquare size={13} /> : <Square size={13} />}
                      <span>Cap. {num}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Active Queue Status */}
        {!!tasks.length && (
          <section className="y-dl-progress-box" aria-live="polite">
            <div className="y-dl-progress-header">
              <strong className="y-dl-stage-text">Descargando a Biblioteca</strong>
              <span className="y-dl-counter-badge">
                {tasks.filter((t) => t.status === 'COMPLETED').length} / {tasks.length} listos
              </span>
            </div>

            <div className="y-dl-tasks-list">
              {tasks.map((task) => (
                <div key={task.id} className="y-dl-task-row">
                  <span className="y-dl-task-name">{task.chapter.name}</span>
                  <span className="y-dl-task-status">
                    {
                      ({
                        QUEUED: 'En cola',
                        DOWNLOADING: 'Descargando...',
                        COMPLETED: 'Guardado',
                        ERROR: 'Error',
                        PAUSED: 'Pausado',
                      })[task.status]
                    }
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {error && (
          <div className="y-dl-error-box" role="alert">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}
      </DialogContent>

      <DialogActions className="y-dl-actions">
        <button type="button" className="nyomiri-btn nyomiri-btn-neutral" onClick={onClose}>
          Cerrar
        </button>
        <button
          type="button"
          className="nyomiri-btn nyomiri-btn-primary"
          disabled={!selectedNumbers.length || isBusy}
          onClick={() => void enqueue()}
        >
          <Download size={15} />
          <span>Descargar {selectedNumbers.length} {selectedNumbers.length === 1 ? 'capítulo' : 'capítulos'}</span>
        </button>
      </DialogActions>
    </Dialog>
  );
}
